/**
 * VELUREA MOTION SYSTEM v2
 * GSAP + Lenis — runs after DOM is ready.
 * sessionStorage key: 'v_intro_done'
 */

(function () {

  // ── WAIT FOR GSAP + LENIS ──────────────────────────────────────
  function waitFor(condition, cb, tries) {
    if (condition()) return cb();
    if ((tries || 0) > 50) return; // give up after ~2.5s
    setTimeout(() => waitFor(condition, cb, (tries || 0) + 1), 50);
  }

  // ── 1. LENIS SMOOTH SCROLL ─────────────────────────────────────
  function initLenis() {
    if (typeof Lenis === 'undefined') return null;
    
    // Disable completely on mobile/touch to prevent iOS Safari conflicts
    if (window.innerWidth <= 768 || ('ontouchstart' in window) || navigator.maxTouchPoints > 0) {
      return null;
    }

    const lenis = new Lenis({
      duration: 1.25,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
      smoothTouch: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // Sync with GSAP ScrollTrigger if available
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(time => lenis.raf(time * 1000));
      gsap.ticker.lagSmoothing(0, 0);
    }

    return lenis;
  }

  // ── 2. SILK GLOW REVEAL INTRO ──────────────────────────────────
  function initIntro(lenis) {
    const overlay = document.getElementById('v-intro');
    if (!overlay) return initGlobalAnimations();

    if (sessionStorage.getItem('v_intro_done')) {
      overlay.remove();
      document.body.classList.remove('v-loading');
      initGlobalAnimations();
      return;
    }

    if (lenis) lenis.stop();

    gsap.registerPlugin(ScrollTrigger);

    const tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      onComplete: () => {
        sessionStorage.setItem('v_intro_done', '1');
        document.body.classList.remove('v-loading');
        if (lenis) lenis.start();
        gsap.to(overlay, {
          autoAlpha: 0,
          duration: 0.8,
          onComplete: () => {
            overlay.remove();
            initGlobalAnimations();
          }
        });
      }
    });

    // 0 – 1s: ambient glow pulses in
    tl.to('.v-intro__glow', {
      opacity: 1, scale: 1, duration: 1.4
    }, 0);

    // 0.5 – 1.5s: silk wave sweeps
    tl.to('.v-intro__silk', {
      opacity: 1, duration: 0.6
    }, 0.5);
    tl.to('#v-silk-path', {
      attr: { d: 'M-10,80 C10,20 25,10 50,50 C75,90 90,30 110,20' },
      duration: 1.8, ease: 'power2.inOut'
    }, 0.6);

    // 1.5 – 2.5s: logo body fades in
    tl.to('.v-intro__body', {
      opacity: 1, filter: 'blur(0px)', y: 0,
      duration: 1.2
    }, 1.5);

    // 1.8s: divider expands
    tl.to('.v-intro__divider', {
      width: 120, duration: 0.9, ease: 'power2.out'
    }, 1.8);

    // 2.5 – 3.5s: tagline rises
    tl.to('.v-intro__tagline', {
      opacity: 1, filter: 'blur(0px)', y: 0,
      duration: 1, ease: 'power2.out'
    }, 2.5);

    // 3.5s: hold, then glow expands and body fades out
    tl.to('.v-intro__glow', {
      scale: 1.8, opacity: 0, duration: 0.9, ease: 'power2.in'
    }, 3.8);
    tl.to('.v-intro__body', {
      opacity: 0, scale: 1.06, filter: 'blur(6px)',
      duration: 0.9, ease: 'power2.in'
    }, 3.8);
  }

  // ── 3. GLOBAL ANIMATIONS (post-intro) ─────────────────────────
  function initGlobalAnimations() {
    if (typeof gsap === 'undefined') return;

    gsap.registerPlugin(ScrollTrigger);

    // Hero stagger
    const heroEls = gsap.utils.toArray(
      '.v-hero-editorial__badge, .v-hero-editorial__title, .v-hero-editorial__subtitle, .v-hero-editorial__actions'
    );
    if (heroEls.length) {
      gsap.fromTo(heroEls,
        { y: 28, opacity: 0, filter: 'blur(5px)' },
        { y: 0, opacity: 1, filter: 'blur(0px)', duration: 1.1, stagger: 0.14, ease: 'power3.out', delay: 0.2 }
      );
    }

    // Hero parallax
    const heroImg = document.querySelector('.v-hero-editorial__media img');
    if (heroImg) {
      gsap.to(heroImg, {
        yPercent: 12, ease: 'none',
        scrollTrigger: { trigger: '.v-hero-editorial', start: 'top top', end: 'bottom top', scrub: true }
      });
    }

    // Section scroll reveals — desktop only, animate cards not whole sections
    const isMobile = window.innerWidth <= 768;
    
    if (!isMobile) {
      // Desktop: animate full sections
      gsap.utils.toArray('.shopify-section').forEach(sec => {
        if (sec.querySelector('.v-hero-editorial')) return;
        gsap.fromTo(sec,
          { y: 24, opacity: 0 },
          {
            y: 0, opacity: 1, duration: 0.7, ease: 'power2.out',
            scrollTrigger: { trigger: sec, start: 'top 90%', toggleActions: 'play none none none' }
          }
        );
      });
    } else {
      // Mobile: only animate individual cards, never full sections
      gsap.utils.toArray('.card-wrapper').forEach(card => {
        gsap.fromTo(card,
          { y: 16, opacity: 0 },
          {
            y: 0, opacity: 1, duration: 0.5, ease: 'power2.out',
            scrollTrigger: { trigger: card, start: 'top 95%', toggleActions: 'play none none none' }
          }
        );
      });
    }

    // Magnetic buttons
    document.querySelectorAll('.v-hero-btn, .button--primary, .product-form__submit').forEach(btn => {
      btn.addEventListener('mousemove', e => {
        const r = btn.getBoundingClientRect();
        gsap.to(btn, { x: (e.clientX - r.left - r.width / 2) * 0.18, y: (e.clientY - r.top - r.height / 2) * 0.18, duration: 0.35, ease: 'power2.out' });
      });
      btn.addEventListener('mouseleave', () => {
        gsap.to(btn, { x: 0, y: 0, duration: 0.65, ease: 'elastic.out(1, 0.4)' });
      });
    });
  }

  // ── BOOT ───────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => {
    waitFor(
      () => typeof gsap !== 'undefined',
      () => {
        const lenis = initLenis();
        initIntro(lenis);
      }
    );
  });

})();
